import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type IEbayAspect,
  type IEbayAspectShort,
  type IEbayMarketplace,
  type IProductEbayDetail,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Button, Col, Divider, Form, Input, Row, Select, Space } from 'antd';
import { type DefaultOptionType } from 'antd/lib/select';
import React, { ReactElement, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { type Dispatch } from 'redux';

import {
  getFilterOption,
  getFilterSort,
} from '../../../fragments/select-filter';
import { useAppDispatch } from '../../../hooks/use-app-dispatch';
import { ebayCategorySaveAspects } from '../../../reducers/ebay/reducer';
import { type TEbayAspectsValues } from '../../../reducers/ebay/types';
import { SPACING } from '../../../theme/tokens';
import Link from '../../link';

const inputNameSeparator = '###';

const onFinishAspects = (
  { code },
  values,
  productUUID: string,
  dispatch: Dispatch,
): void => {
  const vals: TEbayAspectsValues = {};

  Object.keys(values).forEach((key: string): void => {
    const v: string | undefined = values[key];
    const [ebayCategoryId, ebayAspectName] = key.split(inputNameSeparator);

    if (!vals[ebayCategoryId]) {
      vals[ebayCategoryId] = {};
    }

    vals[ebayCategoryId][ebayAspectName] = v;
  });

  dispatch(ebayCategorySaveAspects(productUUID, code, vals));
};

function EbayAspectContainer({
  Marketplace,
}: {
  Marketplace: IEbayMarketplace;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const Ebay = useSelector((s: RootState) => s.Ebay);
  const Product = useSelector((s: RootState) => s.Product);
  const [form] = Form.useForm();

  const [initialValues, setInitialValues] = useState({});
  const [aspectElements, setAspectElements] = useState<React.ReactElement[]>(
    [],
  );

  const productEbay: IProductEbayDetail | null | undefined =
    Ebay.productEbay[Marketplace.code];
  const ebayAspectsJson: string = JSON.stringify(Ebay.aspects);

  useEffect((): void => {
    if (productEbay) {
      productEbay.aspects.forEach((ebayAspect: IEbayAspectShort): void => {
        const key = `${ebayAspect.ebay.categoryId}${inputNameSeparator}${ebayAspect.name}`;

        form.setFieldValue(key, ebayAspect.value);
      });
    }
  }, [productEbay, form]);

  useEffect((): void => {
    if (!Ebay.aspects[Marketplace.code]) {
      return;
    }

    const aspects: IEbayAspect[] = Ebay.aspects[Marketplace.code]
      .sort((a: IEbayAspect, b: IEbayAspect): number =>
        stringCompare(a.name, b.name),
      )
      .sort(
        (a: IEbayAspect, b: IEbayAspect): number =>
          Number(b.required) - Number(a.required),
      );

    const aspectElementsUE: ReactElement[] = aspects
      .map((aspect: IEbayAspect): ReactElement => {
        let inputItem: React.ReactElement;
        let inputItemOptional: React.ReactElement | undefined;

        const inputName = `${aspect.ebay.categoryId}${inputNameSeparator}${aspect.name}`;

        setInitialValues((prev) => {
          return {
            ...prev,
            [inputName]: '',
          };
        });

        if (aspect.ebayAspectItems.length) {
          const options = aspect.ebayAspectItems.map(
            (ebayAspectItem): DefaultOptionType => ({
              value: ebayAspectItem.name,
              label: ebayAspectItem.name,
            }),
          );

          inputItem = (
            <Form.Item
              label={aspect.name}
              name={inputName}
              rules={[{ required: aspect.required }]}
              required={aspect.required}
            >
              <Select
                options={options}
                showSearch={{
                  filterOption: getFilterOption,
                  filterSort: getFilterSort,
                }}
              />
            </Form.Item>
          );

          if (aspect.body.aspectMode === 'FREE_TEXT') {
            inputItemOptional = (
              <Form.Item label={`${aspect.name} free text`} name={inputName}>
                <Input />
              </Form.Item>
            );
          }
        } else {
          inputItem = (
            <Form.Item
              label={aspect.name}
              name={inputName}
              rules={[{ required: aspect.required }]}
              required={aspect.required}
            >
              <Input />
            </Form.Item>
          );
        }

        if (inputItemOptional) {
          return (
            <Row key={aspect.name}>
              <Col xs={24} sm={12}>
                {inputItem}
              </Col>
              <Col xs={24} sm={12}>
                {inputItemOptional}
              </Col>
            </Row>
          );
        }

        return (
          <Row key={aspect.name}>
            <Col xs={24} sm={12}>
              {inputItem}
            </Col>
          </Row>
        );
      })
      .filter(notEmpty);

    setAspectElements(aspectElementsUE);
  }, [Ebay.aspects, Marketplace.code, ebayAspectsJson]);

  return (
    <Form
      key={Marketplace.code}
      onFinish={(values) =>
        onFinishAspects(Marketplace, values, Product.uuid, dispatch)
      }
      disabled={Ebay.inProcess}
      initialValues={initialValues}
      layout="horizontal"
      labelCol={{ span: 4 }}
      wrapperCol={{ span: 14 }}
      form={form}
    >
      <Row key={Marketplace.code}>
        <Col span={24}>
          <Divider titlePlacement="start">
            <Space size={SPACING.lg} align="start">
              {Marketplace.name}
              <Link to={`/ebay/marketplace/${Marketplace.code}`} openInNewTab />
            </Space>
          </Divider>

          {aspectElements.map((aspectElement) => aspectElement)}
        </Col>
      </Row>
      <Row>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Row>
    </Form>
  );
}

export default EbayAspectContainer;
